import React, { useEffect, useState } from "react";
import { Box, keyframes } from "@mui/material";

const TILE_COLORS = {
  2: "#abc9eb",
  4: "#89aed8",
  8: "#6999cf",
  16: "#4c83c2",
  32: "#4ca6c2",
  64: "#59b8a7",
  128: "#82b879",
  256: "#b6bf73",
  512: "#baa5d9",
  1024: "#e0a580",
  2048: "#ff9f43",
};

const getTileColor = (value) => TILE_COLORS[value] || "#fff";

// Animations
const dropIn = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const mergePulse = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.1);
    filter: brightness(1.2);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

const highlightColumn = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(127,214,247,0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(127,214,247,0.4);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(127,214,247,0);
  }
`;

const MiniGridAnimation = ({ step }) => {
  const [animationState, setAnimationState] = useState(0);
  const [board, setBoard] = useState(Array(4).fill(Array(4).fill(null)));

  useEffect(() => {
    // Reset animation state when step changes
    setAnimationState(0);

    // Set initial board state based on step
    switch (step) {
      case 0: // Place blocks
        setBoard([
          [null, null, null, null],
          [null, null, null, null],
          [null, null, null, null],
          [null, null, null, null],
        ]);
        break;
      case 1: // Match & merge
        setBoard([
          [null, null, null, null],
          [null, null, null, null],
          [2, null, null, null],
          [2, null, null, null],
        ]);
        break;
      case 2: // Reach 2048 staggered merges
        setBoard([
          [null, 256, 128, 64],
          [512, 128, 64, 32],
          [512, 64, 32, 16],
          [1024, 8, 4, 2],
        ]);
        break;
    }

    // Start animation sequence
    const interval = setInterval(() => {
      setAnimationState((prev) => {
        let next;
        if (step === 2) {
          next = (prev + 1) % 3;
        } else {
          next = (prev + 1) % 2;
        }
        // For step 1, simulate click and place block
        if (step === 0) {
          if (next === 0) {
            // Start with empty board
            setBoard((prev) => {
              const newBoard = prev.map((row) => [...row]);
              newBoard[3][0] = null;
              return newBoard;
            });
          } else if (next === 1) {
            // Place block immediately after highlight starts
            setBoard((prev) => {
              const newBoard = prev.map((row) => [...row]);
              newBoard[3][0] = 2;
              return newBoard;
            });
          }
        } else if (step === 1) {
          if (next === 0) {
            // Reset to initial state
            setBoard([
              [null, null, null, null],
              [null, null, null, null],
              [2, null, null, null],
              [2, null, null, null],
            ]);
          } else if (next === 1) {
            // Move top block down and merge
            setBoard([
              [null, null, null, null],
              [null, null, null, null],
              [null, null, null, null],
              [4, null, null, null],
            ]);
          }
        } else if (step === 2) {
          if (next === 0) {
            // Initial state
            setBoard([
              [null, 256, 128, 64],
              [512, 128, 64, 32],
              [512, 64, 32, 16],
              [1024, 8, 4, 2],
            ]);
          } else if (next === 1) {
            // First merge: 512s merge into 1024 (row 2), 256 ready to drop
            setBoard([
              [null, 256, 128, 64],
              [null, 128, 64, 32],
              [1024, 64, 32, 16],
              [1024, 8, 4, 2],
            ]);
          } else if (next === 2) {
            // Second merge: 256s merge into 512 (row 2), 1024s merge into 2048 (bottom)
            setBoard([
              [null, 256, 128, 64],
              [null, 128, 64, 32],
              [null, 64, 32, 16],
              [2048, 8, 4, 2],
            ]);
          }
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  const renderTile = (value, row, col) => {
    if (!value) return null;

    const isMerging =
      (step === 1 && row === 3 && col === 0 && animationState === 1) ||
      (step === 2 && row === 2 && col === 0 && animationState === 1) || // 512s merge
      (step === 2 && row === 3 && col === 0 && animationState === 2); // 1024s merge

    const isDropping =
      (step === 1 && row === 2 && col === 0 && animationState === 1) ||
      (step === 2 && row === 0 && col === 0 && animationState === 1) || // 512 drops
      (step === 2 && row === 1 && col === 0 && animationState === 2); // 256 drops

    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          color: "#222",
          background: getTileColor(value),
          borderRadius: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          border: "2px solid #7fd6f7",
          animation: isDropping
            ? `${dropIn} 0.3s ease-out`
            : isMerging
            ? `${mergePulse} 0.3s ease-in-out`
            : "none",
        }}
      >
        {value}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: 200,
        height: 200,
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        columnGap: 1,
        rowGap: 0.5,
        p: 1,
        bgcolor: "#f5f5f5",
        borderRadius: 1,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {board.map((row, rowIdx) =>
        row.map((cell, colIdx) => (
          <Box
            key={`${rowIdx}-${colIdx}`}
            sx={{
              position: "relative",
              width: "100%",
              paddingTop: "100%",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              animation:
                step === 0 && colIdx === 0 && animationState === 0
                  ? `${highlightColumn} 0.3s ease-in-out`
                  : "none",
            }}
          >
            {renderTile(cell, rowIdx, colIdx)}
          </Box>
        ))
      )}
    </Box>
  );
};

export default MiniGridAnimation;
